// 미니게임(열린목장/열린쿠킹) 공통 타입/스키마/순수 헬퍼.
// 설계: docs/game-feature-plan.md. 원칙: 가치 발행(보상/코인/확률/타이머)은 서버 권위.
//   본 모듈은 클라/서버/테스트가 공유하는 순수 계약과 어뷰징 방지용 계산만 담는다(부수효과 없음).

import { z } from "zod";

// 게임 식별자. 라우트/세션/보상 출처 태깅에 쓴다.
export const GAME_NAME = {
  RANCH: "ranch",
  COOKING: "cooking",
} as const;
export type GameName = (typeof GAME_NAME)[keyof typeof GAME_NAME];

// 동물 종(열린목장). 교배는 같은 종 안에서만 일어난다.
export const RANCH_SPECIES = ["cow", "chicken", "pig", "duck", "sheep"] as const;
export type RanchSpecies = (typeof RANCH_SPECIES)[number];

// 품종 등급. 가치/판매가/희귀도 기준.
export const BREED_GRADE = ["common", "rare", "epic", "event"] as const;
export type BreedGrade = (typeof BREED_GRADE)[number];

// 게임 운영 설정(서버 game_settings 행과 1:1). 관리자 조정값.
export interface GameSettingsDto {
  gamesEnabled: boolean;
  ranchEnabled: boolean;
  cookingEnabled: boolean;
  // 보상 정책(사용자 결정: 일일 적립금 상한 100원).
  dailyPointCapKrw: number;
  playtimeRewardKrw: number;
  playtimeRewardMinutes: number;
  // 코인 경제(일반 게임 수준).
  startingCoins: number;
  adoptCostCoins: number;
  // 이벤트 품종 태그별 보상(원). 예: { golden: 50, valentine: 30 }
  eventRewards: Record<string, number>;
}

// GET /games/config 응답(셸/마이페이지 버튼 게이팅용). 설정의 안전한 공개 부분집합.
export interface GameConfigDto {
  gamesEnabled: boolean;
  ranch: { enabled: boolean };
  cooking: { enabled: boolean };
  reward: {
    dailyCapKrw: number;
    playtimeRewardKrw: number;
    playtimeRewardMinutes: number;
  };
}

// 게임 세션 상태(플레이타임 적립 진척 표시용).
export interface GameSessionDto {
  sessionId: string;
  game: GameName;
  accruedSeconds: number;
}

// 보상 청구 결과. 서버가 캡/멱등 적용 후 실제 지급액과 오늘 누계를 돌려준다.
export interface RewardClaimResultDto {
  grantedKrw: number; // 이번 청구로 실제 지급된 적립금(0 가능)
  pointsBalance: number; // 청구 후 잔액
  todayGrantedKrw: number; // 오늘 게임으로 받은 총 적립금
  dailyCapKrw: number; // 일일 상한
  capReached: boolean; // 상한 도달 여부
}

// ---- 입력 스키마 ----

export const gameNameSchema = z.enum([GAME_NAME.RANCH, GAME_NAME.COOKING]);

// 세션 시작.
export const gameSessionStartSchema = z.object({
  game: gameNameSchema,
});
export type GameSessionStartInput = z.infer<typeof gameSessionStartSchema>;

// 하트비트(플레이 지속 핑). 어느 세션인지만 보낸다(경과시간은 서버가 계산).
export const gameHeartbeatSchema = z.object({
  sessionId: z.string().min(1),
});
export type GameHeartbeatInput = z.infer<typeof gameHeartbeatSchema>;

// 보상 청구. kind 별 자격은 서버가 검증한다. ref 는 이벤트/레시피 등에서 대상 식별용(선택).
export const rewardClaimKindSchema = z.enum(["playtime", "event", "recipe"]);
export type RewardClaimKind = z.infer<typeof rewardClaimKindSchema>;
export const rewardClaimSchema = z.object({
  kind: rewardClaimKindSchema,
  ref: z.string().min(1).max(100).optional(),
});
export type RewardClaimInput = z.infer<typeof rewardClaimSchema>;

// ---- 열린목장 DTO ----

// 품종 메타(카탈로그/상세). 실존 품종이면 trivia 노출, 상품 연결 시 productId.
export interface BreedDto {
  id: string;
  species: RanchSpecies;
  name: string;
  grade: BreedGrade;
  isReal: boolean;
  trivia: string | null;
  productId: string | null;
  spriteKey: string | null;
  basePriceCoins: number; // 판매가
  adoptCostCoins: number | null; // 분양 비용(common 품종에만 의미, NULL=기본값 폴백)
  eventTag: string | null;
}

// 분양 옵션 1개(종별). 분양 모달이 칸으로 표시: 이미지(spriteKey)/이름/비용.
export interface AdoptOptionDto {
  species: RanchSpecies;
  breedId: string;
  name: string;
  spriteKey: string | null;
  costCoins: number;
}

// 카탈로그(클라가 동물 렌더/분양 종 선택에 사용). 콘텐츠는 서버 주입.
export interface RanchCatalogDto {
  species: RanchSpecies[];
  adoptCostCoins: number; // 기본 분양 비용(개별 미설정 종의 폴백)
  adoptables: AdoptOptionDto[]; // 종별 분양 옵션(비용 포함)
  breeds: BreedDto[];
}

// 보유 동물 1마리(월드 위치 + 표시 메타).
export interface RanchAnimalDto {
  id: string;
  breedId: string;
  species: RanchSpecies;
  grade: BreedGrade;
  name: string;
  spriteKey: string | null;
  posX: number;
  posY: number;
  busy: boolean; // 진행 중 교배의 부모로 묶여 있으면 true(판매/재교배 불가 표시)
  eventRewardKrw: number | null; // 이벤트 품종이고 미수령이면 교환 가능액(원), 아니면 null
  happiness: number; // 0~RANCH_HAPPINESS_MAX. 일정 이상이어야 교배 가능
  isBaby: boolean; // 아직 성장 중(교배 불가, 작게 표시)
  maturesInSec: number; // 성체까지 남은 초(0=성체)
  petCooldownSec: number; // 쓰다듬기 쿨다운 남은 초(0=가능)
}

// 열린목장 게임플레이 상수(행복도/성장 - 무한 교배 방지). 추후 관리자 설정으로 승격 가능.
export const RANCH_HAPPINESS_MAX = 100;
export const RANCH_BREED_HAPPINESS = 80; // 교배 가능 최소 행복도
export const RANCH_PET_HAPPINESS = 15; // 쓰다듬기 증가량
export const RANCH_PET_COOLDOWN_SEC = 10; // 쓰다듬기 쿨다운(초)
export const RANCH_FEED_HAPPINESS = 40; // 밥주기 증가량
export const RANCH_FEED_COST_COINS = 5; // 밥주기 비용(코인)
export const RANCH_MATURE_SECONDS = 600; // 새끼 성장 시간(10분)

// 쓰다듬기/밥주기 결과.
export interface RanchPetResultDto {
  happiness: number;
}
export interface RanchFeedResultDto {
  happiness: number;
  coins: number;
}

// 진행 중/완료 교배(결과 품종은 수령 전까지 비공개).
export interface RanchBreedingDto {
  id: string;
  parentA: string;
  parentB: string;
  readyAt: string;
  ready: boolean;
  remainingSeconds: number;
}

// 목장 권위 상태(클라는 이걸 받아 렌더).
export interface RanchStateDto {
  coins: number;
  animals: RanchAnimalDto[];
  breedings: RanchBreedingDto[];
  decor: unknown[];
}

// ---- 열린목장 입력 스키마 ----

export const ranchAdoptSchema = z.object({
  species: z.enum(["cow", "chicken", "pig", "duck", "sheep"]),
});
export type RanchAdoptInput = z.infer<typeof ranchAdoptSchema>;

export const ranchBreedSchema = z.object({
  animalIdA: z.string().min(1),
  animalIdB: z.string().min(1),
});
export type RanchBreedInput = z.infer<typeof ranchBreedSchema>;

export const ranchCollectSchema = z.object({ breedingId: z.string().min(1) });
export type RanchCollectInput = z.infer<typeof ranchCollectSchema>;

export const ranchMoveSchema = z.object({
  animalId: z.string().min(1),
  x: z.number().finite(),
  y: z.number().finite(),
});
export type RanchMoveInput = z.infer<typeof ranchMoveSchema>;

export const ranchDecorSchema = z.object({ decor: z.array(z.unknown()).max(300) });
export type RanchDecorInput = z.infer<typeof ranchDecorSchema>;

export const ranchSellSchema = z.object({ animalId: z.string().min(1) });
export type RanchSellInput = z.infer<typeof ranchSellSchema>;

export const ranchPetSchema = z.object({ animalId: z.string().min(1) });
export type RanchPetInput = z.infer<typeof ranchPetSchema>;

export const ranchFeedSchema = z.object({ animalId: z.string().min(1) });
export type RanchFeedInput = z.infer<typeof ranchFeedSchema>;

// ---- 순수 헬퍼(어뷰징 방지 계산) ----

// 하트비트 1회로 인정할 플레이 초.
//  - 너무 짧으면(잦은 핑) 0, 너무 길면(백그라운드/시계 점프) 상한으로 클램프해 시간 조작을 막는다.
export const HEARTBEAT_MIN_CREDIT_SEC = 1;
export const HEARTBEAT_MAX_CREDIT_SEC = 90; // 클라 핑 주기(약 60s)보다 약간 크게

// 직전 핑과의 간격(초)을 인정 가능한 플레이 초로 환산한다(시간 조작 방지 클램프).
export function creditableHeartbeatSeconds(deltaSeconds: number): number {
  if (!Number.isFinite(deltaSeconds) || deltaSeconds < HEARTBEAT_MIN_CREDIT_SEC) {
    return 0;
  }
  return Math.min(Math.floor(deltaSeconds), HEARTBEAT_MAX_CREDIT_SEC);
}

// 누적 플레이 초에서 보상 단위 개수(예: 10분당 1단위).
export function computePlaytimeUnits(accruedSeconds: number, unitMinutes: number): number {
  const unitSec = Math.max(1, Math.floor(unitMinutes)) * 60;
  if (!Number.isFinite(accruedSeconds) || accruedSeconds <= 0) {
    return 0;
  }
  return Math.floor(accruedSeconds / unitSec);
}

// 일일 캡을 적용해 이번에 실제 지급할 금액.
//  - earnedKrw: 자격상 받을 금액, alreadyKrw: 오늘 이미 받은 금액, capKrw: 일일 상한.
export function applyDailyCap(earnedKrw: number, alreadyKrw: number, capKrw: number): number {
  const remaining = Math.max(0, capKrw - Math.max(0, alreadyKrw));
  return Math.max(0, Math.min(earnedKrw, remaining));
}

// 가중치 배열에서 인덱스 1개를 가중 랜덤 선택. 양수 가중치 합이 0이면 -1.
//  - 분양/교배 결과 등 서버 권위 RNG 에 쓴다(클라 신뢰 금지). rng 는 테스트 주입 가능.
export function pickWeightedIndex(weights: number[], rng: () => number = Math.random): number {
  let total = 0;
  for (const w of weights) {
    if (w > 0) {
      total += w;
    }
  }
  if (total <= 0) {
    return -1;
  }
  let r = rng() * total;
  for (let i = 0; i < weights.length; i += 1) {
    const w = weights[i] ?? 0;
    if (w <= 0) {
      continue;
    }
    r -= w;
    if (r < 0) {
      return i;
    }
  }
  return weights.length - 1;
}
